import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { SmartBookingLayout } from '../components/SmartBookingLayout';
import { RootState } from '../redux/store';
import { StepCard } from '../components/StepCard';
import { Button } from '../components/Button';
import { User, Clock, Phone, Mail, Stethoscope, NotebookPen, BadgeCheck, Pill } from 'lucide-react';
import { createAppointment } from '../services/api';
import { SuccessCard } from '../components/SuccessCard';
import { chooseOperatory, formatSelectedSlot } from '../utils/helpers';
import { PatientTypeEnum, PATIENT_TYPE_TO_BACKEND } from '../types';

export const Confirmation = () => {
  const { treatment, patientType, selectedSlot, patientDetails, selectedOperatories, selectedDurationMinutes, patient_request_id } = useSelector((s: RootState) => s.appointment);  
  const { practice } = useSelector((s: RootState) => s.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ time: string; providerName?: string } | null>(null);
  const isExistingPatient = patientType === PatientTypeEnum.Existing;


  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isSmartBooking = useMemo(() => {
    const value = searchParams.get('smart_booking');
    return value === 'true' || value === '1';
  }, [searchParams]);

  const Container = isSmartBooking ? SmartBookingLayout : Layout;

  const book = async () => {
    if (!treatment || !patientType || !selectedDurationMinutes || !selectedSlot) return;
    const payload = {
      treatment_codes: treatment.treatmentCodes,
      patient_type: PATIENT_TYPE_TO_BACKEND[patientType as PatientTypeEnum],
      duration: selectedDurationMinutes,
      reason: patientDetails.notes || '',
      patient_request_id: patient_request_id,
      appointment_at: selectedSlot,
      operatory_id: Array.isArray(selectedOperatories) ? chooseOperatory(selectedOperatories) : {},
      provider_id: treatment.provider.id,
      appointment_type_id: treatment.id,
      tenant_id: practice?.tenant_id
    };
    try {
      setSubmitting(true);
      await createAppointment(payload as any);
      setSuccess({ time: selectedSlot, providerName: treatment.provider ? `${treatment.provider.firstName} ${treatment.provider.lastName}` : undefined });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (isSmartBooking) {
      navigate(`/bookonline/smart-booking${location.search || ''}`);
    } else {
      navigate('/bookonline/step-4');
    }
  };

  return (
    <Container>
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold text-black">Confirmation</h1>
          <p className="text-sm text-gray-500">Thank you for taking the time to book with us. Please review your information below.</p>
        </div>
        <StepCard>
          {success ? <SuccessCard isSmartBooking={isSmartBooking}/> : (
          <div className="space-y-5 py-2">
            {/* Treatment & Patient Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Stethoscope size={20} className="text-blue-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Treatment</div>
                  <div className="text-base font-medium text-black mt-1">{treatment?.title}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <User size={20} className="text-purple-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Patient Type</div>
                  <div className="text-base font-medium text-black mt-1 capitalize">{patientType || '-'}</div>
                </div>
              </div>
            </div>

            {/* Time Slot */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'> 
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock size={20} className="text-amber-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Appointment Time</div>
                  <div className="text-base font-medium text-black mt-1">{(formatSelectedSlot(selectedSlot || ''))}</div>
                </div>
              </div>
             <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <NotebookPen size={20} className="text-amber-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Appointment Note</div>
                  <div className="text-sm font-medium text-black mt-1">{patientDetails.notes || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100"></div>

            {/* Contact Information */}
            <div>
              <div className='flex gap-3'>
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Contact Information</h3>
                {isExistingPatient && 
                  <div className='flex justify-center item-center badge badge-success gap-1'>
                    <BadgeCheck size={16} fill='white' className='text-success-600'/>
                    <p className='text-white text-xs font-semibold'>Record Found</p>
                  </div>
                }
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <User size={18} className="text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Name</div>
                    <div className="text-sm font-medium text-black">
                      {patientDetails.firstName} {patientDetails.lastName}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Phone</div>
                    <div className="text-sm font-medium text-black">{patientDetails.phone || '-'}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 md:col-span-2">
                  <Mail size={18} className="text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Email</div>
                    <div className="text-sm font-medium text-black">{patientDetails.email || '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </StepCard>

        {/* Action Buttons */}
        { !success && (
        <div className="flex justify-between">
          <Button title='Back' variant='secondary' onClick={handleBack}/>
          <Button 
            title={submitting ? 'Booking…' : 'Confirm Appointment'} 
            disabled={submitting} 
            onClick={book}
          />
        </div>
        )}
      </div>
    </Container>
  );
};